import { useContext, useState } from "react";
import { useTranslation } from "@coworker/locales";
import { ModalContext } from "../../context/ModalContextProvider";
import { ModalType } from "../../context/modalType";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { useProductQualityTeamId } from "@coworker/app/src/hooks/useProductQualityTeamId";
import { TaskTypesRoutes } from "../../../home/constants/taskTypesRoutes";
import styles from "./main-navigation.module.css";
import { isRDTDevice, isRDTDevice11 } from "@coworker/reusable/helpers/devices";
import { FixaListView } from "../../../shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "../../../shared/wrappers/FixaListView/FixaListViewItem";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { ProductQualityError } from "../../../shared/components/ProductQualityError/productQualityError";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useLoggedInUser } from "@coworker/app/src/core/auth/useLoggedInUser";
import { UserRoles } from "@coworker/apprestructured/src/shared/enums/user/userRoles";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import GearIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/GearIconPath";
import {
  NavigationItem,
  useNavigationItems,
} from "./hooks/useMainNavigationItems";
import useHasTeamEditorPermissions from "@coworker/apprestructured/src/teams-management/hooks/useTeamEditorPermissions";

const MainNavigation = () => {
  const navigate = useFixaNavigate();
  const { t } = useTranslation();
  const { modalState, toggleModal } = useContext(ModalContext);
  const qualityTeamId = useProductQualityTeamId();
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const isRDT11 = isRDTDevice11();
  const isRDT = isRDTDevice();

  const [, setSearchParams] = useSearchParamState();
  const { data: taskTypes } = useAllTaskTypes();
  const enableCustomTaskV2 = useFlag(FLAGS.ENABLE_CUSTOM_TASK_V2);
  const enableProductIssueV2 = useFlag(FLAGS.ENABLE_PRODUCT_ISSUE_V2);

  const { getRoleId } = useLoggedInUser();
  const isGlobalSuperUser = getRoleId() === UserRoles.SUPERUSER;
  const enableTeamsManagerNavigation = useHasTeamEditorPermissions();

  const navigationItems = useNavigationItems({
    isRDT: isRDT || isRDT11,
    enableTeamsManagerNavigation,
    taskTypes,
    enableCustomTaskV2,
    enableProductIssueV2,
  });

  const handleToggleMainNavigation = () => {
    toggleModal({ type: ModalType.MainNavigation });
  };

  function getTaskTypeId(taskTypeName: string): string | undefined {
    return taskTypes?.find((type) => type.name === taskTypeName)?.id;
  }

  const handleNavigationLinkClick = async (type: string) => {
    if (type === TaskTypesRoutes.PRODUCT_QUALITY && !qualityTeamId) {
      setOpenErrorModal(true);
      return;
    }

    if (type === TaskTypesRoutes.CUSTOM) {
      const customTaskId = getTaskTypeId(TaskTypeNames.CUSTOM_TASK);

      if (enableCustomTaskV2 && customTaskId) {
        setSearchParams((prev) => {
          prev.set("taskCreator", String(customTaskId));
        });
      } else {
        navigate("/task/new/custom");
      }
      handleToggleMainNavigation();
      return;
    }

    if (type === TaskTypesRoutes.PRODUCT_ISSUE && enableProductIssueV2) {
      const productIssueId = getTaskTypeId(TaskTypeNames.PRODUCT_ISSUE);

      if (productIssueId) {
        setSearchParams((prev) => {
          prev.set("taskCreator", String(productIssueId));
        });
      }
      handleToggleMainNavigation();
      return;
    }

    navigate(`/scanner/${type}`);
    handleToggleMainNavigation();
  };

  const handleItemClick = async (item: NavigationItem) => {
    if (item.href) {
      navigate(item.href);
    }

    if (item.link) {
      window.open(item.link, "_blank", "noopener,noreferrer");
    }

    if (item.id === "task-overview") {
      trackerHelper.trackTaskLinkClickedInMenu();
    }

    handleToggleMainNavigation();
  };

  return (
    <FixaModal
      visible={modalState.isMainNavigationOpen}
      keepContentMounted={true}
      handleCloseBtn={handleToggleMainNavigation}
    >
      <FixaSheets
        labelledById="main-navigation-header"
        header={
          <FixaModalHeader
            id="main-navigation-header"
            title={t("menuString")}
            closeBtnClick={() => handleToggleMainNavigation()}
          />
        }
        footer={null}
        preserveAlignment={false}
        size="small"
        alignment="left"
      >
        <FixaListView
          id="main-navigation"
          className={styles["list-view"] as string}
        >
          {navigationItems.map((item) => (
            <FixaListViewItem
              inset
              onClick={() => {
                if (item.taskType) {
                  handleNavigationLinkClick(item.taskType);
                } else {
                  handleItemClick(item);
                }
              }}
              key={item.id}
              emphasised
              control="navigational"
              controlIcon={null}
              id={item.id}
              leadingIcon={item.icon}
              title={t(item.labelTranslationKey)}
              size="small"
              data-testid={item.testId}
              disabled={item.disabled}
            />
          ))}
          <ProductQualityError
            openProductQualityErrorModal={openErrorModal}
            onClose={setOpenErrorModal}
          />
          {isGlobalSuperUser && (
            <FixaListViewItem
              inset
              onClick={() => {
                navigate("/scanner-debug");
                handleToggleMainNavigation();
              }}
              key="scanner-debug"
              emphasised
              control="navigational"
              controlIcon={null}
              id="scanner-debug"
              leadingIcon={GearIconPath}
              title="Scanner Debug"
              size="small"
              data-testid="scannerDebugNavigationButton"
            />
          )}
        </FixaListView>
      </FixaSheets>
    </FixaModal>
  );
};

export default MainNavigation;
